'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

const motionQuery = '(prefers-reduced-motion: reduce)';
function subscribeToMotion(onChange: () => void) {
  const query = window.matchMedia(motionQuery);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

export function useReducedMotion() {
  return useSyncExternalStore(
    subscribeToMotion,
    () => window.matchMedia(motionQuery).matches,
    () => false
  );
}

type ClockOptions = {
  key: string;
  duration: number;
  running: boolean;
  reduced: boolean;
  step?: number;
};

/** A paused clock retains elapsed time; changing the scene or replaying resets it. */
export function useSequenceClock({
  key,
  duration,
  running,
  reduced,
  step = 40,
}: ClockOptions) {
  const elapsed = useRef({ key, milliseconds: 0 });
  const [frame, setFrame] = useState({ key, time: 0 });
  const [replay, setReplay] = useState(0);

  useEffect(() => {
    if (elapsed.current.key !== key) elapsed.current = { key, milliseconds: 0 };
    if (!running || reduced) return;
    let animationFrame = 0;
    let previous = performance.now();
    const tick = (now: number) => {
      elapsed.current.milliseconds = Math.min(
        duration,
        elapsed.current.milliseconds + Math.min(now - previous, 250)
      );
      previous = now;
      const time =
        elapsed.current.milliseconds >= duration
          ? duration
          : Math.floor(elapsed.current.milliseconds / step) * step;
      setFrame((current) =>
        current.key === key && current.time === time ? current : { key, time }
      );
      if (time < duration) animationFrame = requestAnimationFrame(tick);
    };
    const syncVisibility = () => {
      cancelAnimationFrame(animationFrame);
      if (!document.hidden) {
        previous = performance.now();
        animationFrame = requestAnimationFrame(tick);
      }
    };
    syncVisibility();
    document.addEventListener('visibilitychange', syncVisibility);
    return () => {
      cancelAnimationFrame(animationFrame);
      document.removeEventListener('visibilitychange', syncVisibility);
    };
  }, [key, duration, running, reduced, step, replay]);

  const restart = useCallback(() => {
    elapsed.current = { key, milliseconds: 0 };
    setFrame({ key, time: 0 });
    setReplay((value) => value + 1);
  }, [key]);
  const time = reduced ? duration : frame.key === key ? frame.time : 0;
  return { t: time, done: time >= duration, restart };
}

export function useCollectionClock(
  options: Omit<ClockOptions, 'key'> & { id: string }
) {
  return useSequenceClock({ ...options, key: options.id }).t;
}

export function usePlaybackClock(options: Omit<ClockOptions, 'key'>) {
  return useSequenceClock({ ...options, key: 'playback' });
}

/** Return a stable callback ref so only the observer owns the mutable DOM reference. */
export function useMostlyInView(share = 0.6) {
  const element = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState<boolean | null>(null);
  const attach = useCallback((node: HTMLDivElement | null) => {
    element.current = node;
  }, []);
  useEffect(() => {
    if (!element.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const viewportHeight = entry.rootBounds?.height ?? window.innerHeight;
        setVisible(
          entry.isIntersecting &&
            (entry.intersectionRatio >= share ||
              entry.intersectionRect.height >= viewportHeight * share)
        );
      },
      { threshold: [0, 0.2, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
    );
    observer.observe(element.current);
    return () => observer.disconnect();
  }, [share]);
  return [attach, visible] as const;
}
